import {useCallback, useEffect, useRef, useState} from "react";

import {
    deleteFact as deleteFactRequest,
    getConversation,
    getMessages,
    handleEscalation as handleEscalationRequest,
    putFact as putFactRequest,
    release as releaseRequest,
    reply as replyRequest,
    sendProducts as sendProductsRequest,
    setMarketingOptOut as setMarketingOptOutRequest,
    suspend as suspendRequest,
    reactivate as reactivateRequest,
    getPersonalData as getPersonalDataRequest,
    erase as eraseRequest,
    takeOver as takeOverRequest,
} from "api/personalShopper";

/**
 * The poll reads the Marketplace API's own mirror of the conversation (kept up to date by
 * Sylius' pushes), not Sylius itself: each tick is one cheap local read, and a hidden tab
 * does not tick at all.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * The summary as it reads once this message is the latest of the thread: the same rule as
 * Sylius's ShopperConversation::getStatus(), applied locally until the next detail reload.
 */
function withLastMessage(conversation, message) {
    const inbound = message.direction === "inbound";
    const awaitingHuman = inbound && !!conversation.handledBy;
    const toHandle = awaitingHuman || (conversation.openEscalations || []).length > 0;

    return {
        ...conversation,
        lastMessage: {id: message.id, author: message.author, content: message.content, createdAt: message.createdAt},
        lastMessageAt: message.createdAt,
        lastInboundAt: inbound ? message.createdAt : conversation.lastInboundAt,
        awaitingHuman,
        status: toHandle ? "to_handle" : (conversation.convertedAt ? "converted" : "open"),
    };
}

/**
 * One conversation kept live: the detail is loaded once, then the messages newer than the
 * last one seen are polled every few seconds while the tab is visible, along with who holds
 * the conversation. Every action updates the local state from the API answer so the screen
 * never waits for a poll.
 */
export default function useConversation(id) {
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pollingBroken, setPollingBroken] = useState(false);
    const lastIdRef = useRef(0);

    const applyMessages = useCallback((items) => {
        if (!items || items.length === 0) {
            return;
        }
        setMessages((previous) => {
            const known = new Set(previous.map((message) => message.id));
            const fresh = items.filter((message) => !known.has(message.id));

            return fresh.length === 0 ? previous : [...previous, ...fresh];
        });
        lastIdRef.current = Math.max(lastIdRef.current, ...items.map((message) => message.id));
        // The summary follows the thread: the last message, when, and whether the shopper is
        // now waiting for the person holding the conversation (what the list's card shows).
        const latest = items[items.length - 1];
        setConversation((previous) => (previous ? withLastMessage(previous, latest) : previous));
    }, []);

    const reload = useCallback(() => {
        setLoading(true);
        setError(null);

        return getConversation(id)
            .then((detail) => {
                const {messages: initialMessages, ...rest} = detail;
                setConversation(rest);
                setMessages([]);
                lastIdRef.current = 0;
                applyMessages(initialMessages);
            })
            .catch((requestError) => setError(requestError))
            .finally(() => setLoading(false));
    }, [id, applyMessages]);

    useEffect(() => {
        reload();
    }, [reload]);

    const isLoaded = conversation !== null;

    useEffect(() => {
        if (!isLoaded) {
            return undefined;
        }
        let active = true;
        const poll = () => {
            if (document.hidden) {
                return;
            }
            getMessages(id, lastIdRef.current)
                .then((data) => {
                    if (!active) {
                        return;
                    }
                    applyMessages(data.items);
                    setPollingBroken(false);
                    setConversation((previous) => {
                        if (!previous || (previous.handledBy === data.handledBy && previous.customerServiceWindowOpen === data.customerServiceWindowOpen)) {
                            return previous;
                        }

                        return {...previous, handledBy: data.handledBy, customerServiceWindowOpen: data.customerServiceWindowOpen};
                    });
                })
                .catch(() => active && setPollingBroken(true));
        };
        // A hidden tab stops polling altogether and catches up the moment it is shown again.
        const onVisibilityChange = () => !document.hidden && poll();
        document.addEventListener("visibilitychange", onVisibilityChange);
        const timer = setInterval(poll, POLL_INTERVAL_MS);

        return () => {
            active = false;
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [id, isLoaded, applyMessages]);

    const mergeSummary = (summary) => setConversation((previous) => ({...previous, ...summary}));

    const takeOver = (force = false) => takeOverRequest(id, force).then(mergeSummary);
    const release = () => releaseRequest(id).then(mergeSummary);
    const reply = (text) => replyRequest(id, text).then((message) => {
        applyMessages([message]);

        return message;
    });
    const sendProducts = (codes) => sendProductsRequest(id, codes).then((data) => {
        applyMessages(data.items || []);

        return data.items || [];
    });
    const handleEscalation = (escalationId) => handleEscalationRequest(escalationId).then((escalation) => {
        setConversation((previous) => {
            const escalations = previous.escalations.map((item) => (item.id === escalation.id ? escalation : item));

            const openEscalations = escalations.filter((item) => item.status === "open").map((item) => item.category);
            const toHandle = previous.awaitingHuman || openEscalations.length > 0;

            return {
                ...previous,
                escalations,
                openEscalations,
                status: toHandle ? "to_handle" : (previous.convertedAt ? "converted" : "open"),
            };
        });
    });
    const putFact = (label, value) => putFactRequest(id, label, value).then((fact) => {
        setConversation((previous) => {
            const others = previous.facts.filter((item) => item.label !== fact.label);

            return {...previous, facts: [...others, fact].sort((a, b) => a.label.localeCompare(b.label))};
        });
    });
    const deleteFact = (label) => deleteFactRequest(id, label).then(() => {
        setConversation((previous) => ({...previous, facts: previous.facts.filter((item) => item.label !== label)}));
    });
    const setMarketingOptOut = (optedOut) => setMarketingOptOutRequest(id, optedOut).then(mergeSummary);
    const suspend = (reason) => suspendRequest(id, reason).then(mergeSummary);
    const reactivate = () => reactivateRequest(id).then(mergeSummary);
    const getPersonalData = () => getPersonalDataRequest(id);
    const erase = () => eraseRequest(id);

    return {conversation, messages, loading, error, pollingBroken, reload, takeOver, release, reply, sendProducts, handleEscalation, putFact, deleteFact, setMarketingOptOut, suspend, reactivate, getPersonalData, erase};
}
